import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const Virgoyearly = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for VIRGO
  const staticMeta = {
    title: "AstroBhagya – Free Virgo Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Virgo yearly horoscope predictions 2026? Discover free Virgo astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Virgo yearly horoscope predictions 2026, Virgo yearly Astrology predictions, Virgo annual predictions, Yearly horoscope by Virgo zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchVirgoYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `virgo_yearly_${dateStr}`; 
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        const virgoData = data.find(
          item => item.id === 35 || item.sunShine?.toUpperCase().includes("VIRGO")
        );

        if (virgoData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, virgoData);
          localStorage.setItem(cacheKey, JSON.stringify(virgoData));
          setHoroscopeData(virgoData);
        } else {
          throw new Error("Virgo data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Virgo yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Virgo Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchVirgoYearly(retries - 1);
      } else {
        // FALLBACK DATA - VIRGO 2026 
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Virgo 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 combines Virgo practicality with opportunity. Strategic planning and calculated risks open new doors in career and relationships.</p>
            <h2>Love and Relationships</h2>
            <p>Deeper emotional understanding strengthens bonds. Meaningful connections built on trust and intellectual compatibility.</p>
            <h2>Career and Finances</h2>
            <p>Strategic thinking brings steady advancement. Careful budgeting and informed investments ensure financial security.</p>
            <h2>Health & Wellness</h2>
            <p>Balanced routine prevents burnout. Meditation and stress management maintain mental clarity.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Wear Emerald:</strong> Enhances intellect and decision-making clarity.</li>
              <li><strong>Spiritual Practice:</strong> Meditation strengthens inner peace.</li>
              <li><strong>Saturn Remedies:</strong> Donate green items on Saturdays for progress.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVirgoYearly();
  }, [fetchVirgoYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Virgo 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchVirgoYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Virgo 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Virgooo.webp")}
                alt="Virgo"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/leo" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Leo Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/libra" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Libra Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Virgo Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Virgo?",
                  answer: "Virgo individuals are known for their analytical mind, practicality, attention to detail, and service-oriented nature. Ruled by Mercury, they excel in organization and problem-solving.",
                },
                {
                  question: "2. What can Virgo expect in love and relationships in 2026?",
                  answer: "2026 brings deeper emotional connections. Meaningful relationships built on trust, intellectual compatibility, and honest communication.",
                },
                {
                  question: "3. What does 2026 look like for Virgo career growth?",
                  answer: "Strategic thinking drives steady advancement. Discipline and long-term focus bring professional recognition.",
                },
                {
                  question: "4. What health advice for Virgo in 2026?",
                  answer: "Balanced routine prevents burnout. Regular exercise, nutrition, and meditation maintain physical/mental health.",
                },
                {
                  question: "5. Key astrological remedies for Virgo?",
                  answer: "Emerald stone, meditation practice, and Saturday green donations enhance clarity and progress.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Virgoyearly; 
